const validations = {
  // Common

  mobile: ({ required = false } = {}) => ({
    required,
    pattern: /^[6-9]\d{9}$/,
    minLength: 10,
    maxLength: 10,
    isInteger: true,
    message1:
      'Contact number must be a valid 10-digit mobile number starting with 6–9',
    message: 'Please enter a valid 10-digit Contact No.',
  }),
  email: ({ required = false } = {}) => ({
    required,
    maxLength: 45,
    pattern: /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/,
    message: 'Please enter a valid Email Address',
  }),
  address: ({ required = false } = {}) => ({
    required,
    pattern: /^[A-Za-z0-9 ,.&/-]{10,70}$/,
    minLength: 10,
    maxLength: 70,
    message:
      'Address must be 10–70 characters and may contain letters, numbers, spaces, -, ., /, , and &',
  }),
  pincode: ({ required = false } = {}) => ({
    required,
    maxLength: 6,
    minLength: 6,
    pattern: /^\d{6}$/,
    message: 'Pin Code must be exactly 6 digits',
  }),
  pan: ({ required = false } = {}) => ({
    required,
    pattern: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
    message: 'Invalid PAN format (e.g., ABCDE1234F)',
    maxLength: 10,
  }),
  gst: ({ required = false } = {}) => ({
    required,
    pattern: /^\d{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[A-Z0-9]{1}Z[A-Z0-9]{1}$/,
    message: 'Invalid GST format (e.g., 22AAAAA0000A1Z5)',
    maxLength: 15,
  }),
  description: ({ required = false, minLength = 3, maxLength = 80 } = {}) => ({
    required,
    minLength,
    maxLength,
    pattern: new RegExp(`^(?=.{${minLength},${maxLength}}$)[A-Za-z0-9 ]+$`),
    message: `Description must be ${minLength}-${maxLength} characters and may contain only letters, numbers, and spaces.`,
  }),
  percentage: ({
    required = false,
    decimalPlace = 2,
    minLength = 1,
    maxLength = 4,
  } = {}) => ({
    required,
    minLength,
    maxLength,
    isDecimal: decimalPlace > 0,
    isInteger: decimalPlace === 0,
    decimalPlace,
    pattern: new RegExp(
      `^(100(\\.0{1,${decimalPlace}})?|[1-9]?\\d(\\.\\d{1,${decimalPlace}})?)$`,
    ),
    message: `Please enter a valid tax percentage between 0 and 100 ${
      decimalPlace > 0 ? ` allow up to ${decimalPlace} decimal places` : ''
    }*`,
  }),
  invoiceNumber: ({ required = false } = {}) => ({
    required,
    pattern: /^[A-Za-z0-9\/_-]{3,20}$/,
    message:
      "Please enter a valid invoice number (3–20 characters, alphanumeric with '/', '-', or '_', no spaces).",
    minLength: 3,
    maxLength: 30,
  }),
  modeOfTransportation: ({ required = false } = {}) => ({
    required,
    pattern: /^[A-Za-z ]{3,30}$/,
    minLength: 3,
    maxLength: 30,
    message:
      'Mode of transportation must be 3–30 characters and contain only letters and spaces',
  }),
  invoiceRemark: ({ required = false } = {}) => ({
    required,
    pattern: /^[A-Za-z0-9\s.,\-\/()!?:]{3,80}$/,
    minLength: 3,
    maxLength: 80,
    message:
      'Remark can contain characters between 3 and 80 and may include letters, numbers, spaces, and basic punctuation (.,-/()).',
  }),

  // Product Management

  productName: ({ required = false } = {}) => ({
    required,
    minLength: 2,
    maxLength: 40,
    pattern: /^[A-Za-z]+(?: [A-Za-z]+)*$/,
    message:
      'Product name must be 2–40 characters long and contain only alphabets and spaces',
  }),
  model: ({ required = false } = {}) => ({
    required,
    pattern: /^(?=.*[A-Za-z0-9])[A-Za-z0-9 ()]{2,30}$/,
    minLength: 2,
    maxLength: 30,
    message:
      'Model Name must be 2–30 characters long and can contain only letters, numbers, spaces, parentheses',
  }),
  variant: ({ required = false } = {}) => ({
    required,
    pattern: /^[A-Za-z0-9 !@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]{5,80}$/,
    minLength: 5,
    maxLength: 80,
    message: 'Variant Name must be 5-80 characters long and can contain spaces',
  }),
  businessPartnerId: ({ required = false } = {}) => ({
    required,
    pattern: /^[A-Za-z0-9 ]*$/,
    message: 'Only alphabets and digits are allowed',
  }),
  colour: ({ required = false } = {}) => ({
    required,
    pattern: /^[A-Za-z ]{3,30}$/,
    minLength: 3,
    maxLength: 30,
    message:
      'Color name must be 3–30 characters and contain only letters and spaces',
  }),
  batteryName: ({ required = false } = {}) => ({
    required,
    maxLength: 40,
    minLength: 5,
  }),
  batteryCode: ({ required = false } = {}) => ({
    required,
    maxLength: 10,
    minLength: 4,
  }),
  batteryBrandName: ({ required = false } = {}) => ({
    required,
    minLength: 5,
    maxLength: 60,
    pattern: /^[A-Za-z0-9\s]{5,60}$/,
    message:
      'Battery Brand must be 5–60 characters long and can contain only alphabets, numbers',
  }),
  price: ({
    required = false,
    decimalPlace = 2,
    minLength = 2,
    maxLength = 6,
  } = {}) => {
    const integerPart = `[1-9]\\d{${minLength - 1},${maxLength - 1}}`;

    const pattern =
      decimalPlace > 0
        ? new RegExp(`^${integerPart}(\\.\\d{1,${decimalPlace}})?$`)
        : new RegExp(`^${integerPart}$`);

    return {
      required,
      minLength,
      maxLength,
      decimalPlace,
      isDecimal: decimalPlace > 0,
      isInteger: decimalPlace === 0,
      pattern,
      message: `Price must be ${minLength}–${maxLength} digits long${
        decimalPlace > 0
          ? ` and allow up to ${decimalPlace} decimal places`
          : ''
      } and must not start with 0`,
    };
  },
  taxName: ({ required = false } = {}) => ({
    required,
    pattern: /^[A-Za-z]+(?: [A-Za-z]+)*$/,
    minLength: 3,
    maxLength: 20,
    message:
      'Tax Name must contain at least 3 alphabets and can include only alphabets and spaces',
  }),
  itemName: ({ required = false } = {}) => ({
    required,
    pattern: /^(?=.*[A-Za-z0-9])[A-Za-z0-9 -]+$/,
    minLength: 3,
    maxLength: 40,
    message:
      'Item Name must be 3–40 characters long and can contain only letters, numbers, spaces, and hyphens (-)',
  }),
  campaignName: ({ required = false } = {}) => ({
    required,
    pattern: /^(?=(?:.*[A-Za-z]){1,})(?=.{8,50}$)[A-Za-z\s'-]+$/,
    minLength: 8,
    maxLength: 50,
    message:
      'Campaign Name must be 8–50 characters long and can contain only letters, spaces, hyphens, or apostrophes',
  }),
  duration: ({
    required = false,
    minLength = 1,
    maxLength = 3,
    isInteger = true,
    pattern = /^(?:[1-9]|[1-9][0-9]|[1-5][0-9]{2}|600)$/,
    message = 'Duration must be a positive number between 1 and 600 with no leading zeros',
  } = {}) => ({
    required,
    pattern,
    isInteger,
    minLength,
    maxLength,
    message,
  }),
  quantity: ({
    required = false,
    minLength = 1,
    maxLength = 3,
    pattern = /^[1-9][0-9]{0,2}$/,
  } = {}) => ({
    required,
    pattern,
    isInteger: true,
    minLength,
    maxLength,
    message: `Quantity must be a positive number with ${minLength}–${maxLength} digits.`,
  }),
  hsnName: ({ required = false } = {}) => ({
    required,
    isInteger: true,
    pattern: /^(?!0{8})[0-9]{8}$/,
    minLength: 8,
    maxLength: 8,
    message: 'HSN must be exactly 8 digits and cannot be all zeros',
  }),
  unitName: ({ required = false } = {}) => ({
    required,
    pattern: /^(?=.*[A-Za-z0-9])[A-Za-z0-9 ()]{2,30}$/,
    minLength: 2,
    maxLength: 30,
    message:
      'Unit name must be 2–30 characters and may contain letters, numbers, spaces, and parentheses',
  }),
  financierName: ({ required = false } = {}) => ({
    required,
    pattern: /^(?=(?:.*[A-Za-z]){3,})[A-Za-z\s]+$/,
    minLength: 3,
    maxLength: 25,
    message:
      'Finance name must contain at least 3 alphabets and only alphabets and spaces are allowed',
  }),
  warrantyName: ({ required = false, minLength = 3, maxLength = 50 } = {}) => ({
    required,
    minLength,
    maxLength,
    pattern: /^[A-Za-z0-9]+(?: [A-Za-z0-9]+)*$/,
    message: `Warranty name must be ${minLength}–${maxLength} characters long and contain only letters, numbers, and spaces.`,
  }),
  warrantyValue: ({ required = false, minLength = 1, maxLength = 4 } = {}) => ({
    required,
    minLength,
    maxLength,
    isInteger: true,
    pattern: /^[1-9]\d*$/,
    message: `Value must be a non-negative whole number between ${minLength} and ${maxLength} digits.`,
  }),
  termName: ({ required = false, minLength = 5, maxLength = 40 } = {}) => ({
    required,
    minLength,
    maxLength,
    pattern: /^[A-Za-z0-9/!&-]+$/,
    message: `Termset name must be ${minLength}–${maxLength} characters long, without spaces, and may contain only letters, numbers, and the special characters - / ! &.`,
  }),

  // Dealer Management

  dealerCode: ({ required = false } = {}) => ({
    required,
    maxLength: 25,
  }),
  dealerName: ({ required = false } = {}) => ({
    required,
    minLength: 5,
    maxLength: 50,
    pattern: /^[A-Za-z]+(?:[ &-][A-Za-z]+)*$/,
    message:
      'Dealer name must be 5–50 characters and may contain letters, spaces, hyphens, or &',
  }),
};

export default validations;
